/* eslint
  "styled-components-a11y/label-has-associated-control": [
    "error", { "controlComponents": ["InputComponent"] },
  ],
*/

import type React from 'react';
import { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../hooks';
import Smooch from 'smooch';

// Redux & Actions
import { useUpdateConversation } from 'components/App/hooks';
import { startNewIntentSelection } from 'actions/workflows-v2/workflowsV2';

// Components
import FormWrapper from 'components/FormWrapper/FormWrapper';
import UserMessage from 'components/UserMessage';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import ListDropdown from 'components/ListDropdown/ListDropdown';
import OptionButton from 'components/OptionButton';
import MultiLevelDropdown from 'components/MultiLevelDropdown/MultiLevelDropdown';

// Types
import type {
  ComponentTypes,
  ContextVariables,
  FormFields,
  FormStepComponent,
  FormStepComponentText,
} from 'api/types';

// Utils
import {
  TextField,
  SelectDropdown,
} from '@forethought-technologies/forethought-elements';

import {
  isEmailValid,
  isPhoneNumberValid,
  isNumberValid,
  isName,
  getShortTextErrorMessage,
  getAutocompleteValue,
  getEmailAutocompleteValue,
  getErrorMessage,
} from 'utils/utils';
import {
  addScreenReaderNotification,
  selectConversationId,
  selectFeatureFlags,
  selectIsAuthorIconVisible,
  selectIsLiveChatMode,
  selectTouchedFormFields,
  setDismissableNotification,
  setTouchedFormField,
  setTouchedFormFields,
} from 'reducers/workflows';
import {
  selectChatId,
  selectIntegrationConfig,
  selectLiveChatIntegration,
} from 'reducers/live-chat';
import { Box } from '@mui/material';
import { IrArticleTextMessage } from 'components/IrArticleTextMessage/IrArticleTextMessage';
import { sendForm } from 'components/salesforce-messaging-one-chat/messagingService';
import { v4 as uuidv4 } from 'uuid';
import { addLiveChatEventMessage } from 'actions/live-chat';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';
import { getSmoochErrorMessage } from 'embed/smooch';
import { getSmoochLocalStorage } from 'components/App/hooks/useHandleSuncoInitialization';

interface FormStepProps {
  isLastStep: boolean;
  setStepContextVariables: Function;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  /** Components to display inside the form */
  widgetComponent: FormStepComponent;
}

const FormStep: React.FC<React.PropsWithChildren<FormStepProps>> = ({
  isLastStep,
  setStepContextVariables,
  setStepSent,
  stepIndex,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  const updateConversation = useUpdateConversation();
  const isLiveChatMode = useAppSelector(selectIsLiveChatMode);
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const chatId = useAppSelector(selectChatId);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );
  const featureFlags = useAppSelector(selectFeatureFlags);
  const conversationId = useAppSelector(selectConversationId);
  const { integrationId } = useAppSelector(selectIntegrationConfig);
  const shouldHideNeverMindButton = featureFlags.includes(
    'hide_never_mind_button',
  );
  const showRequiredLabel = !featureFlags.includes(
    'hide_required_form_input_labels',
  );

  const disableSubmitOnEnter =
    widgetComponent.component_fields.form_fields.findIndex(
      field =>
        field.input_type === 'LONG_TEXT' || field.input_type === 'DROPDOWN',
    ) !== -1;

  const componentFields = widgetComponent.component_fields;
  const componentText = componentFields.forethought_text;
  const contextVariables = useMemo(
    () => widgetComponent.contextVariables || {},
    [widgetComponent.contextVariables],
  );
  const formFields = componentFields.form_fields;
  const neverMindSelection = widgetComponent?.neverMindSelection || '';

  const [dropdownLabel, setDropdownLabel] = useState<string>('');

  const validContextVariables = formFields.filter((field: FormFields) => {
    const outputVariable = (contextVariables[field.output_variable] ?? '')
      .toString()
      .trim();

    if (!outputVariable) {
      return false;
    }

    if (field.input_type === 'EMAIL') {
      return isEmailValid(outputVariable);
    }

    if (field.input_type === 'PHONE_NUMBER' || field.input_type === 'PHONE') {
      return isPhoneNumberValid(outputVariable);
    }

    if (field.input_type === 'NUMBER') {
      return isNumberValid(outputVariable);
    }

    if (field.input_type === 'SHORT_TEXT' && field.configuration_fields) {
      return !Boolean(
        getShortTextErrorMessage(
          outputVariable,
          field.configuration_fields,
          componentText,
        ),
      );
    }

    return true;
  });

  const isDisabled = formFields.length !== validContextVariables.length;

  const isSent = !!widgetComponent.isSent;
  const isReroutedByNegativeFeedback =
    !!widgetComponent.isReroutedByNegativeFeedback;

  const inputChange = (
    outputVariable: string,
    value: ContextVariables[string],
  ) => {
    const newState = Object.assign({}, contextVariables);
    newState[outputVariable] = value;
    dispatch(setStepContextVariables(newState));
    dispatch(setTouchedFormField(outputVariable));
  };

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (isDisabled) {
      dispatch(
        setTouchedFormFields(
          formFields.map(({ output_variable }) => output_variable),
        ),
      );

      return;
    }

    if (isLiveChatMode) {
      switch (liveChatIntegration) {
        case 'sunco':
          try {
            await Smooch.sendMessage(
              {
                // @ts-expect-error Smooch types are outdated, `fields` is actually supported
                fields: widgetComponent.component_fields.form_fields.map(
                  ({ output_variable, placeholder }) => ({
                    label: placeholder,
                    name: output_variable,
                    text: contextVariables[output_variable],
                    type: 'text',
                  }),
                ),
                quotedMessageId:
                  widgetComponent.component_fields.quotedMessageId,
                type: 'formResponse',
              },
              chatId,
            );
            dispatch(setDismissableNotification(''));
          } catch (error) {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(setDismissableNotification(errorMessage));
            sendErrorToSentry({
              additionalContext: {
                ...getSmoochLocalStorage(integrationId),
              },
              conversationId: conversationId,
              error: `Sunco Live Chat failed to send form response -- ${getSmoochErrorMessage(error)}`,
            });
          }
          break;
        case 'salesforce':
          sendForm(
            chatId,
            widgetComponent.component_fields.form_fields.map(
              ({ input_type, output_variable }) => {
                function getInputValue() {
                  if (input_type === 'NUMBER') {
                    return {
                      integerValue: Number(contextVariables[output_variable]),
                      valueType: 'IntegerValue',
                    } as const;
                  }

                  if (input_type === 'DATE') {
                    return {
                      dateValue: String(contextVariables[output_variable]),
                      valueType: 'DateValue',
                    } as const;
                  }

                  return {
                    textValue: String(contextVariables[output_variable]),
                    valueType: 'TextValue',
                  } as const;
                }

                return {
                  inputId: output_variable,
                  ...(input_type === 'LIST' ||
                  input_type === 'MULTI_SELECT_LIST'
                    ? {
                        inputValueType: 'SelectedOptionsInputValue',
                        selectedOptions: (
                          (Array.isArray(contextVariables[output_variable])
                            ? contextVariables[output_variable]
                            : contextVariables[output_variable]
                              ? [contextVariables[output_variable]]
                              : []) as string[]
                        ).map(value => ({ optionIdentifier: value })),
                      }
                    : {
                        inputValueType: 'SingleInputValue',
                        value: getInputValue(),
                      }),
                } as const;
              },
            ),
            uuidv4(),
            widgetComponent.component_fields.quotedMessageId || '',
          ).catch((err: unknown) => {
            const errorMessage = 'Message failed to send';
            dispatch(addScreenReaderNotification(errorMessage));
            dispatch(addLiveChatEventMessage({ message: errorMessage }));
            sendErrorToSentry({
              additionalContext: {
                chatId,
                componentFields: widgetComponent.component_fields,
              },
              conversationId,
              error: `Salesforce One Chat: ${errorMessage} -- ${getErrorMessage(err)}`,
            });
          });
          break;
      }

      setStepSent();

      return;
    }

    setStepSent();
    updateConversation(contextVariables);
  };

  const moreThanOneField = formFields.length > 1;

  const isNeverMindButtonSelected = neverMindSelection.length > 0;

  if (isReroutedByNegativeFeedback) {
    return (
      componentFields.prompt && (
        <AgathaMessage
          isAuthorIconVisible={isAuthorIconVisible}
          stepIndex={stepIndex}
        >
          {componentFields.should_render_as_markdown ? (
            <IrArticleTextMessage message={componentFields.prompt} />
          ) : (
            componentFields.prompt
          )}
        </AgathaMessage>
      )
    );
  }

  if (isSent) {
    return (
      <>
        {componentFields.prompt && (
          <AgathaMessage
            agentIcon={componentFields.agentIcon}
            agentName={componentFields.agentName}
            hideTimestamp={widgetComponent.hideTimestamp}
            isAuthorIconVisible={isAuthorIconVisible}
            isLiveChat={widgetComponent.isInAgentConvo}
            stepIndex={stepIndex}
            timestamp={widgetComponent.timestamp}
          >
            {componentFields.should_render_as_markdown ? (
              <IrArticleTextMessage message={componentFields.prompt} />
            ) : (
              componentFields.prompt
            )}
          </AgathaMessage>
        )}

        <UserMessage stepIndex={stepIndex}>
          {isNeverMindButtonSelected
            ? neverMindSelection
            : formFields?.map((component: FormFields, index: number) => {
                const outputVariable = component.output_variable;
                const isDropdown = component.input_type === 'DROPDOWN';
                const isList = component.input_type === 'LIST';
                const outputVariableValue = contextVariables[outputVariable];

                function getFieldValue() {
                  if (isList) {
                    return (
                      component.list_type_options?.find(
                        option => option.value === outputVariableValue,
                      )?.label || ''
                    );
                  }
                  if (isDropdown) {
                    return dropdownLabel;
                  }

                  if (outputVariableValue === true) {
                    return componentText.checkbox_yes;
                  }

                  if (outputVariableValue === false) {
                    return componentText.checkbox_no;
                  }

                  if (component.input_type === 'MULTI_SELECT_LIST') {
                    const options = component.list_type_options || [];
                    const selectedValues = (contextVariables[outputVariable] ||
                      []) as string[];

                    return selectedValues
                      .map(val => {
                        const option = options.find(
                          option => option.value === val,
                        );

                        return option?.label || val;
                      })
                      .join(', ');
                  }

                  return outputVariableValue;
                }

                return (
                  <div key={'componentFields' + index}>
                    {moreThanOneField && (
                      <FieldName isFirstElement={index === 0}>
                        {component.placeholder + ':'}
                      </FieldName>
                    )}
                    <FieldValue>{getFieldValue()}</FieldValue>
                  </div>
                );
              })}
        </UserMessage>
      </>
    );
  }

  return (
    <>
      <FormWrapper
        className='js-form-step'
        disableSubmitOnEnter={disableSubmitOnEnter}
        formTitle={componentFields.prompt || ''}
        isDisabled={false}
        isLastStep={isLastStep}
        onSubmit={onSubmit}
        shouldRenderTitleAsMarkdown={
          !!componentFields.should_render_as_markdown
        }
        stepIndex={stepIndex}
        submitButtonText={componentText.submit_button}
      >
        {formFields?.map((component: FormFields, index: number) => {
          const inputType = component.input_type;
          const outputVariable = component.output_variable;

          return (
            /**
             * TODO: if minHeight 68px fixes android collapsing then we should move
             * this styling to the forethought-elements InputComponent, 68 takes
             * into account the height of the label and the input.
             */
            <Box key={inputType + index} sx={{ minHeight: '68px' }}>
              <Label
                as={inputType === 'DROPDOWN' ? 'div' : undefined}
                dir='auto'
              >
                <InputComponent
                  component={component}
                  componentText={componentText}
                  contextVariables={contextVariables}
                  inputChange={inputChange}
                  inputType={inputType}
                  outputVariable={outputVariable}
                  setDropdownLabel={setDropdownLabel}
                  showRequiredLabel={showRequiredLabel}
                />
              </Label>
            </Box>
          );
        })}
      </FormWrapper>
      {/* Do not show "Never mind" button for One Chat: */}
      {!widgetComponent.isInAgentConvo && !shouldHideNeverMindButton && (
        <Box sx={{ paddingX: '8px' }}>
          <OptionButton
            margin='0 auto 16px 0'
            onClick={() => {
              dispatch(
                startNewIntentSelection(
                  widgetComponent.component_fields.forethought_text.never_mind,
                ),
              );
            }}
            text={widgetComponent.component_fields.forethought_text.never_mind}
          />
        </Box>
      )}
    </>
  );
};

export default FormStep;

const InputComponent = ({
  component,
  componentText,
  contextVariables,
  inputChange,
  inputType,
  outputVariable,
  setDropdownLabel,
  showRequiredLabel,
}: {
  component: FormFields;
  componentText: FormStepComponentText;
  contextVariables: ContextVariables;
  inputChange: (
    outputVariable: string,
    value: ContextVariables[string],
  ) => void;
  inputType: ComponentTypes;
  outputVariable: string;
  setDropdownLabel: (dropdownLabel: string) => void;
  showRequiredLabel: boolean;
}) => {
  const autocompleteValue = useMemo(
    () => getAutocompleteValue(component.placeholder),
    [component.placeholder],
  );
  const emailAutocompleteValue = useMemo(
    () => getEmailAutocompleteValue(component.placeholder),
    [component.placeholder],
  );
  const touchedFormFields = useAppSelector(selectTouchedFormFields);

  const value = contextVariables[outputVariable];
  const valueAsString = (value || '').toString();
  const hasValue = value !== undefined;
  const isFormFieldTouched = touchedFormFields.includes(outputVariable);
  const requiredError =
    valueAsString.trim() === '' ? componentText.required_error : '';
  const isErrorVisible = isFormFieldTouched || hasValue;

  if (inputType === 'SHORT_TEXT')
    return (
      <TextField
        autoCapitalize={isName(component.placeholder) ? 'words' : undefined}
        autoComplete={autocompleteValue}
        dir='auto'
        error={
          isErrorVisible &&
          (requiredError ||
            (component.configuration_fields &&
              getShortTextErrorMessage(
                valueAsString,
                component.configuration_fields,
                componentText,
              )))
        }
        label={component.placeholder}
        maxLength={340}
        onChange={e => inputChange(outputVariable, e.target.value)}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        value={valueAsString}
      />
    );
  if (inputType === 'LONG_TEXT')
    return (
      <>
        <TextField
          autoComplete={autocompleteValue}
          dir='auto'
          error={isErrorVisible && requiredError}
          label={component.placeholder}
          multiline
          onChange={e => {
            inputChange(outputVariable, e.target.value);
          }}
          requiredLabel={componentText.required_label}
          rows={4}
          showRequiredLabel={showRequiredLabel}
          value={valueAsString}
        />
      </>
    );
  if (inputType === 'EMAIL')
    return (
      <TextField
        autoCapitalize='none'
        autoComplete={emailAutocompleteValue}
        autoCorrect='off'
        dir='auto'
        error={
          isErrorVisible &&
          (requiredError ||
            (!isEmailValid(valueAsString) && componentText.email_error))
        }
        label={component.placeholder}
        maxLength={340}
        onChange={e => inputChange(outputVariable, e.target.value)}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        type='email'
        value={valueAsString}
      />
    );

  if (inputType === 'LIST' && !!component.list_type_options) {
    return (
      <ListDropdown
        error={isErrorVisible ? requiredError : ''}
        id={outputVariable}
        label={component.placeholder}
        onChange={e => {
          inputChange(outputVariable, e.target.value);
        }}
        options={component.list_type_options}
        placeholder={componentText.checkbox_placeholder}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        value={(value || '') as string}
      />
    );
  }

  if (inputType === 'DROPDOWN')
    return (
      <>
        {component.placeholder}
        <MultiLevelDropdown
          backButtonText={componentText.dropdown_back}
          dropdownValues={component.dropdown_options || []}
          onChange={(value: string, dropdownLabel: string) => {
            setDropdownLabel(dropdownLabel);
            inputChange(outputVariable, value);
          }}
        />
      </>
    );
  if (inputType === 'PHONE_NUMBER' || inputType === 'PHONE')
    return (
      <TextField
        autoCapitalize='none'
        autoComplete='tel'
        autoCorrect='off'
        dir='auto'
        error={
          isErrorVisible &&
          (requiredError ||
            (!isPhoneNumberValid(valueAsString) && componentText.phone_error))
        }
        label={component.placeholder}
        // 23 would be the longest number with plenty of formatting, like
        // +9 (999) 99-99-99-99-99
        maxLength={23}
        onChange={e => inputChange(outputVariable, e.target.value)}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        type='tel'
        value={valueAsString}
      />
    );
  if (inputType === 'NUMBER')
    return (
      <TextField
        autoComplete={autocompleteValue}
        dir='auto'
        error={
          isErrorVisible &&
          (requiredError ||
            (!isNumberValid(valueAsString) && componentText.number_error))
        }
        label={component.placeholder}
        onChange={e => inputChange(outputVariable, parseInt(e.target.value))}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        value={valueAsString}
      />
    );
  if (inputType === 'CHECKBOX')
    return (
      <SelectDropdown
        error={
          isErrorVisible &&
          (value === undefined ? componentText.required_error : '')
        }
        id={outputVariable}
        label={component.placeholder}
        onChange={e => inputChange(outputVariable, e.target.value === 'Yes')}
        options={[
          {
            label: componentText.checkbox_yes,
            value: 'Yes',
          },
          {
            label: componentText.checkbox_no,
            value: 'No',
          },
        ]}
        placeholder={componentText.checkbox_placeholder}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        value={value === true ? 'Yes' : value === false ? 'No' : ''}
      />
    );

  if (inputType === 'DATE') {
    return (
      <TextField
        autoComplete={autocompleteValue}
        dir='auto'
        error={isErrorVisible && requiredError}
        label={component.placeholder}
        onChange={e => {
          inputChange(outputVariable, e.target.value);
        }}
        requiredLabel={componentText.required_label}
        showRequiredLabel={showRequiredLabel}
        type='date'
        value={valueAsString}
      />
    );
  }

  if (inputType === 'MULTI_SELECT_LIST' && !!component.list_type_options) {
    return (
      <>
        <ListDropdown
          error={isErrorVisible ? requiredError : ''}
          id={outputVariable}
          label={component.placeholder}
          multiple
          onChange={e => {
            inputChange(outputVariable, e.target.value);
          }}
          options={component.list_type_options}
          placeholder=''
          requiredLabel={componentText.required_label}
          showRequiredLabel={showRequiredLabel}
          value={
            (Array.isArray(value) ? value : value ? [value] : []) as string[]
          }
        />
      </>
    );
  }

  // fall back to text field
  return (
    <TextField
      autoComplete={autocompleteValue}
      dir='auto'
      error={isErrorVisible && requiredError}
      label={component.placeholder}
      onChange={e => inputChange(outputVariable, e.target.value)}
      requiredLabel={componentText.required_label}
      showRequiredLabel={showRequiredLabel}
      value={valueAsString}
    />
  );
};

const FieldName = styled('p')<{ isFirstElement: boolean }>`
  margin: ${props => (props.isFirstElement ? '0' : '16px 0 0;')};
`;

const FieldValue = styled('p')`
  margin: 0;
`;

const Label = styled('label')`
  display: block;
  font-size: inherit;
`;
